import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const INVITE_ACCEPTED_STORAGE_KEY = 'siyak_invite_accepted';

export const VerifyRegistrationEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');
  const token = searchParams.get('token');
  const inviteToken = searchParams.get('invite_token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Missing verification token.');
      return;
    }

    const verify = async () => {
      try {
        const res = await axios.post(`${API_URL}/api/auth/verify-registration-email`, { token });
        const { user } = res.data;

        // Accept invite if present
        let inviteAccepted = false;
        let acceptedPropertyTitle = null;
        if (inviteToken) {
          try {
            const acceptResponse = await axios.post(`${API_URL}/api/invites/accept`, { token: inviteToken });
            inviteAccepted = acceptResponse.data?.status === 'accepted';
            acceptedPropertyTitle = acceptResponse.data?.property_title || null;
          } catch (inviteErr) {
            const detail = inviteErr.response?.data?.detail;
            if (detail) toast.error(detail);
          }
        }

        const refreshedUser = await refreshUser(true);
        if (inviteAccepted) {
          try {
            sessionStorage.setItem(INVITE_ACCEPTED_STORAGE_KEY, JSON.stringify({
              propertyTitle: acceptedPropertyTitle || refreshedUser?.property_title || 'your property',
              acceptedAt: new Date().toISOString(),
            }));
          } catch (error) {}
        }
        setStatus('success');
        setMessage(inviteAccepted
          ? `You have successfully joined ${acceptedPropertyTitle || refreshedUser?.property_title || 'your property'}.`
          : `Welcome to SIYAK, ${user?.name || 'there'}! Your email has been verified.`
        );

        const role = String(user?.role || '').toLowerCase();
        setTimeout(() => {
          if (role === 'landlord') navigate('/landlord');
          else if (role === 'tenant') navigate('/tenant');
          else navigate('/');
        }, 2000);
      } catch (err) {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Verification failed. The link may have expired.');
      }
    };

    verify();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <Layout>
      <div className="min-h-screen bg-navy-50 flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md" data-testid="verify-registration-card">
          <CardHeader className="text-center">
            <CardTitle className="text-xl">Email Verification</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            {status === 'verifying' && (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-10 w-10 animate-spin text-navy-600" />
                <p className="text-muted-foreground">Verifying your email...</p>
              </div>
            )}
            {status === 'success' && (
              <div className="flex flex-col items-center gap-3">
                <CheckCircle className="h-10 w-10 text-green-600" />
                <p className="text-green-700 font-medium">{message}</p>
                <p className="text-sm text-muted-foreground">Redirecting you now...</p>
              </div>
            )}
            {status === 'error' && (
              <div className="flex flex-col items-center gap-3">
                <XCircle className="h-10 w-10 text-red-500" />
                <p className="text-red-600 font-medium">{message}</p>
                <Button onClick={() => navigate('/register')} data-testid="back-to-register-btn">
                  Back to Register
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
